(() => {
  const base = window.Shopify?.routes?.root || '/';
  const text = window.RRSetCartText || {};
  const errorText = () => text.error || 'Your cart could not be updated. Please refresh and try again.';
  const props = item => item.properties || {};
  const isSet = item => props(item)._builder === 'dream-v1' && !!props(item)['Included bags'];
  const group = (cart, item) => props(item)._kit_id ? cart.items.filter(other => props(other)._kit_id === props(item)._kit_id) : [item];
  const signature = items => JSON.stringify(items.map(item => [item.key, item.quantity]).sort());
  const request = async (path, body) => {
    const response = await fetch(base + path, {method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', headers: {'Accept': 'application/json', ...(body ? {'Content-Type': 'application/json'} : {})}, ...(body ? {body: JSON.stringify(body)} : {})});
    const data = await response.json();
    if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || errorText());
    return data;
  };
  const get = () => request('cart.js');
  const remove = items => request('cart/update.js', {updates: Object.fromEntries(items.map(item => [item.key, 0]))});
  const findCurrent = (cart, original) => cart.items.find(item => item.key === original.key) || cart.items.find(item => item.variant_id === original.variant_id && JSON.stringify(props(item)) === JSON.stringify(props(original)));
  const load = async (key, productId, transition) => {
    const cart = await get();
    const item = cart.items.find(item => item.key === key && isSet(item));
    if (!item) throw new Error(text.missing || 'This set has changed or is no longer in your cart. Return to your cart to edit it.');
    const items = group(cart, item);
    const currentSignature = signature(items);
    const sameProduct = String(item.product_id) === String(productId) || String(props(item)._rr_source_product) === String(productId);
    if (transition && (String(transition.sourceProductId) !== String(item.product_id) || transition.signature !== currentSignature)) throw new Error(text.changed || 'Your cart changed while you were editing. Return to your cart and reopen this set.');
    if (!sameProduct && !transition) throw new Error(text.missing || 'This set has changed or is no longer in your cart. Return to your cart to edit it.');
    return {item, items, signature: currentSignature};
  };
  const replace = async (context, replacements) => {
    let cart = await get();
    const original = cart.items.find(item => item.key === context.item.key);
    if (!original || signature(group(cart, original)) !== context.signature) throw new Error(text.changed || 'Your cart changed while you were editing. Return to your cart and reopen this set.');
    const oldItems = group(cart, original);
    const sameItems = oldItems.length === replacements.length && oldItems.every(item => replacements.some(wanted => wanted.id === item.variant_id && wanted.quantity === item.quantity));
    if (sameItems) {
      try {
        for (const before of oldItems) {
          const wanted = replacements.find(line => line.id === before.variant_id);
          const properties = {...wanted.properties, _kit_id: props(before)._kit_id};
          const current = findCurrent(cart, before);
          cart = await request('cart/change.js', {id: current.key, quantity: before.quantity, properties});
        }
        return cart;
      } catch (error) {
        cart = await get();
        for (const before of oldItems) {
          const current = cart.items.find(line => line.variant_id === before.variant_id && props(line)._kit_id === props(before)._kit_id);
          if (current) cart = await request('cart/change.js', {id: current.key, quantity: before.quantity, properties: props(before)});
        }
        throw error;
      }
    }
    const newId = replacements[0].properties._kit_id;
    try {
      await request('cart/add.js', {items: replacements.slice().reverse()});
      cart = await get();
      const added = cart.items.filter(item => props(item)._kit_id === newId);
      if (added.length !== replacements.length || added.some(item => !replacements.some(wanted => wanted.id === item.variant_id && wanted.quantity === item.quantity))) throw new Error(errorText());
      await remove(oldItems.map(item => findCurrent(cart, item)).filter(Boolean));
    } catch (error) {
      cart = await get();
      const oldRemain = oldItems.some(item => findCurrent(cart, item));
      const added = cart.items.filter(item => props(item)._kit_id === newId);
      if (!oldRemain && added.length === replacements.length) return cart;
      if (added.length) await remove(added);
      throw error;
    }
    return get();
  };
  const change = async (key, quantity, sections) => {
    let cart = await get();
    const item = cart.items.find(item => item.key === key);
    if (!item) throw new Error(text.changed || errorText());
    if (!isSet(item)) return request('cart/change.js', {id: key, quantity, sections});
    const items = group(cart, item);
    if (quantity === 0) return request('cart/update.js', {updates: Object.fromEntries(items.map(line => [line.key, 0])), sections});
    try {
      for (const original of items) {
        const current = findCurrent(cart, original);
        const units = Number(props(original)._kit_units) || original.quantity / item.quantity;
        const target = Math.round(units * quantity);
        cart = await request('cart/change.js', {id: current.key, quantity: target, sections});
        const changed = findCurrent(cart, original);
        if (!changed || changed.quantity !== target) throw new Error(text.stock || 'That quantity is not available for the complete set.');
      }
      return cart;
    } catch (error) {
      cart = await get();
      for (const original of items.slice().reverse()) {
        const current = findCurrent(cart, original);
        if (current) cart = await request('cart/change.js', {id: current.key, quantity: original.quantity, sections});
      }
      throw error;
    }
  };
  const prepareCart = () => {
    document.querySelectorAll('[data-rr-cart-label]').forEach(bubble => {
      const trigger = bubble.closest('[drawer-to-open="cart-drawer"]');
      if (trigger) trigger.setAttribute('aria-label', bubble.dataset.rrCartLabel);
    });
    const linkedSets = new Set([...document.querySelectorAll('[data-rr-edit-set]')].map(edit => edit.dataset.rrKit).filter(Boolean));
    document.querySelectorAll('[data-rr-edit-set]').forEach(edit => {
      const row = edit.closest('drawer-item');
      if (!row) return;
      row.querySelectorAll('a[href*="/products/"]').forEach(link => {if (link.href !== edit.href) link.href = edit.href;});
      row.querySelectorAll('[remove-lineitem]').forEach(button => button.setAttribute('aria-label', text.remove || 'Remove set and its extras'));
    });
    document.querySelectorAll('[data-rr-linked-extra]').forEach(note => {
      const row = note.closest('drawer-item');
      if (!row) return;
      const linked = linkedSets.has(note.dataset.rrKit);
      note.hidden = !linked;
      row.querySelectorAll('quantity-selector').forEach(control => {control.hidden = linked;});
    });
  };
  window.RRSetCart = {get, load, replace, change, signature};
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {queued = false; prepareCart();});
  }).observe(document.body, {childList: true, subtree: true});
  prepareCart();
})();
